import {
  Box,
  Button,
  Collapse,
  Flex,
  Link,
  Select,
  Text,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import SectionWrapper from './SectionWrapper';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import LanguagesWrapper from './LanguagesWrapper';
import LocalisedField from './LocalisedField';
import AddMoreButton from './AddMoreButton';
import { useState, useMemo, useEffect, useRef, useContext } from 'react';
import {
  SUPPORTED_LOCALES,
  SupportedLocale,
  getLocaleDisplayName,
} from '../../../constants/locales';
import {
  addLanguage,
  setDefaultLanguage,
} from '../../../store/redux/slices/template.slice';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import ChangeDefaultLanguageModal from './modals/ChangeDefaultLanguageModal';

const LanguagesFeature = () => {
  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const meState = meStateRaw;
  const localisationEnabled = meState?.localisationEnabled;

  const dispatch = useAppDispatch();
  const fields = useAppSelector((s) => s.template.fields);
  const defaultLanguage = useAppSelector((s) => s.template.defaultLanguage);
  const languages = useMemo(() => {
    const languageSet = new Set<string>();

    Object.values(fields).forEach((sectionFields) => {
      Object.values(sectionFields).forEach((field) => {
        if (field?.localisation) {
          Object.keys(field.localisation).forEach((langCode) => {
            languageSet.add(langCode);
          });
        }
      });
    });

    return Array.from(languageSet).reduce(
      (acc, langCode) => {
        acc[langCode] = {};
        return acc;
      },
      {} as Record<string, Record<string, never>>
    );
  }, [fields]);

  const [defaultLanguageState, setDefaultLanguageState] = useState<string>(
    defaultLanguage || 'en'
  );

  const availableLocalesForDefault = useMemo(() => {
    const addedLanguages = Object.keys(languages);
    return SUPPORTED_LOCALES.filter(
      (locale) =>
        !addedLanguages.includes(locale) || locale === defaultLanguageState
    );
  }, [languages, defaultLanguageState]);

  const availableLocales = useMemo(() => {
    const addedLanguages = Object.keys(languages);
    return SUPPORTED_LOCALES.filter(
      (locale) =>
        !addedLanguages.includes(locale) && locale !== defaultLanguageState
    );
  }, [languages, defaultLanguageState]);

  const MAX_LANGUAGES = 5;
  const limitReached = Object.keys(languages).length >= MAX_LANGUAGES;
  const [newLanguage, setNewLanguage] = useState(false);
  const newLanguageRef = useRef<HTMLDivElement>(null);
  const scrollToNewLanguage = () => {
    setTimeout(() => {
      const el = newLanguageRef.current;
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 20;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 350);
  };
  const [selectedLanguage, setSelectedLanguage] = useState<
    SupportedLocale | ''
  >('');
  const [newlyAddedLanguage, setNewlyAddedLanguage] = useState<string | null>(
    null
  );
  const [isDefaultOpen, setIsDefaultOpen] = useState(false);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(true);
  const [isChangingDefault, setIsChangingDefault] = useState(false);
  const [expandedLanguage, setExpandedLanguage] = useState<string | null>(null);
  const defaultLanguageRef = useRef<HTMLDivElement>(null);

  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: { defaultLanguage: defaultLanguageRef },
  });

  useEffect(() => {
    if (
      newlyAddedLanguage &&
      Object.keys(languages).includes(newlyAddedLanguage)
    ) {
      const timer = setTimeout(() => {
        setNewlyAddedLanguage(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [languages, newlyAddedLanguage]);

  useEffect(() => {
    if (
      selectedLanguage &&
      availableLocales.length > 0 &&
      !availableLocales.includes(selectedLanguage)
    ) {
      setSelectedLanguage('');
    }
  }, [availableLocales, selectedLanguage]);

  useEffect(() => {
    if (defaultLanguageState !== defaultLanguage) {
      dispatch(setDefaultLanguage(defaultLanguageState));
    }
  }, [defaultLanguageState, defaultLanguage, dispatch]);

  const { handleFocus, handleBlur } =
    useTemplateFieldValidation('defaultLanguage');

  const getCompletionCount = (langCode: string) => {
    let total = 0;
    let completed = 0;
    const sections = ['header', 'central', 'details', 'back'] as const;
    sections.forEach((section) => {
      Object.values(fields[section] || {}).forEach((field) => {
        if (!field || (!field.label && !field.defaultValue)) return;
        if (field.label) total++;
        if (field.defaultValue) total++;
        const loc = field.localisation?.[langCode];
        if (loc?.label) completed++;
        if (loc?.defaultValue) completed++;
      });
    });
    return { total, completed };
  };

  return (
    <SectionWrapper
      id="languages-section"
      title="Languages"
      tooltip="Add up to five languages to your pass template to localise the pass content."
      subtitle={
        <Text>
          Translate your pass into additional languages. Untranslated fields
          will show in {getLocaleDisplayName(defaultLanguageState)}.{' '}
          <Link
            color="linkBlue"
            href="https://docs.passentry.com/documentation/passes/localisation/guide"
            target="_blank"
          >
            Learn more
          </Link>
        </Text>
      }
      locked={false}
      autoOpenOnceWhen={Object.keys(languages).length > 0}
    >
      {/* Default language */}
      <Box
        border={isDefaultOpen ? '1px solid' : 'none'}
        borderColor="lightGrey"
        borderRadius={isDefaultOpen ? 'md' : 'none'}
        py={isDefaultOpen ? 4 : 0}
        px={isDefaultOpen ? 4 : 0}
        my={2}
      >
        <Flex
          alignItems="center"
          justify="space-between"
          cursor="pointer"
          onClick={() => setIsDefaultOpen(!isDefaultOpen)}
        >
          <Text fontSize="16px" textStyle="bodyFamilyMedium">
            Default language
          </Text>
          {isDefaultOpen ? (
            <ChevronUpIcon boxSize="20px" />
          ) : (
            <ChevronDownIcon boxSize="20px" />
          )}
        </Flex>
        <Collapse in={isDefaultOpen}>
          <FieldEditIndicator
            fieldRef={defaultLanguageRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="25px"
          >
            <Flex direction="column" gap={2} mt={1}>
              <Text fontSize="12px" color="grey3">
                English recommended as a fallback
              </Text>
              <Divider borderColor="lightGrey" my={1} />
              <Flex justify="space-between" align="center">
                <Text fontSize="14px">
                  {getLocaleDisplayName(defaultLanguageState)}
                </Text>
                <Link
                  fontSize="12px"
                  color="orangePrimary"
                  textDecoration="underline"
                  _hover={{ textDecoration: 'none' }}
                  onClick={() => setIsChangingDefault(true)}
                >
                  Change
                </Link>
              </Flex>
            </Flex>
          </FieldEditIndicator>
        </Collapse>
      </Box>
      <ChangeDefaultLanguageModal
        isOpen={isChangingDefault}
        onClose={() => setIsChangingDefault(false)}
        onConfirm={(language) => {
          setDefaultLanguageState(language);
        }}
        availableLocales={availableLocalesForDefault}
        currentDefault={defaultLanguageState}
      />

      <Divider borderColor="lightGrey" my={4} />

      {/* Additional languages */}
      <Flex
        alignItems="center"
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsAdditionalOpen(!isAdditionalOpen)}
        mb={3}
      >
        <Text fontSize="16px" textStyle="bodyFamilyMedium">
          Additional languages
        </Text>
        {isAdditionalOpen ? (
          <ChevronUpIcon boxSize="20px" />
        ) : (
          <ChevronDownIcon boxSize="20px" />
        )}
      </Flex>
      <Collapse in={isAdditionalOpen}>
        <Flex direction="column" gap={3}>
          {Object.keys(languages).length === 0 && !newLanguage ? (
            <Box
              border="1px solid"
              borderColor="lightGrey"
              borderRadius="md"
              p={6}
              textAlign="center"
              bg="#F8F8F8"
            >
              <Text fontSize="14px" mb={4}>
                No languages added yet
              </Text>
              <Button
                bg="#3A4A57"
                color="white"
                size="sm"
                px={6}
                _hover={{ bg: '#2f3e4a' }}
                onClick={() => {
                  setNewLanguage(true);
                  scrollToNewLanguage();
                }}
              >
                Add language
              </Button>
            </Box>
          ) : (
            <>
              {Object.keys(languages).map((language) => {
                const { total, completed } = getCompletionCount(language);
                return (
                  <LanguagesWrapper
                    key={language}
                    language={language}
                    completionText={
                      total > 0 && completed < total
                        ? `${completed}/${total} fields complete`
                        : undefined
                    }
                    compact
                    useNewDesign
                    isOpen={expandedLanguage === language}
                    onToggle={(lang) =>
                      setExpandedLanguage(
                        expandedLanguage === lang ? null : lang
                      )
                    }
                  />
                );
              })}
              {newLanguage && (
                <Box
                  ref={newLanguageRef}
                  p={3}
                  border="1px solid"
                  borderColor="lightGrey"
                  borderRadius="md"
                >
                  <Text fontWeight="500" mb={3}>
                    {selectedLanguage
                      ? getLocaleDisplayName(selectedLanguage)
                      : '[Language]'}
                  </Text>
                  <Select
                    placeholder="Select"
                    value={selectedLanguage || ''}
                    onChange={(e) => {
                      const locale = e.target.value as SupportedLocale;
                      if (locale) {
                        setSelectedLanguage(locale);
                        dispatch(addLanguage(locale));
                        setNewLanguage(false);
                        setNewlyAddedLanguage(locale);
                        setExpandedLanguage(locale);
                      }
                    }}
                    bg="#EEEEEE"
                    border="none"
                    borderRadius="md"
                    fontSize="14px"
                    size="sm"
                  >
                    {availableLocales.map((locale) => (
                      <option key={locale} value={locale}>
                        {getLocaleDisplayName(locale)}
                      </option>
                    ))}
                  </Select>
                  <Flex direction="column" gap={3} mt={3}>
                    {Object.keys(fields.header).map((field, index) =>
                      fields.header[field]?.label ||
                      fields.header[field]?.defaultValue ? (
                        <LocalisedField
                          key={field}
                          section="header"
                          field={field}
                          fieldData={fields.header[field]}
                          language={selectedLanguage || '__pending__'}
                          showLabel={!!fields.header[field]?.label}
                          showDefaultValue={
                            !!fields.header[field]?.defaultValue
                          }
                          sectionName={`Header Field ${index + 1}`}
                          compact
                          disabled={!selectedLanguage}
                        />
                      ) : null
                    )}
                    {Object.keys(fields.central).map((field, index) =>
                      fields.central[field]?.label ||
                      fields.central[field]?.defaultValue ? (
                        <LocalisedField
                          key={field}
                          section="central"
                          field={field}
                          fieldData={fields.central[field]}
                          language={selectedLanguage || '__pending__'}
                          showLabel={!!fields.central[field]?.label}
                          showDefaultValue={
                            !!fields.central[field]?.defaultValue
                          }
                          sectionName={`Central Field ${index + 1}`}
                          compact
                          disabled={!selectedLanguage}
                        />
                      ) : null
                    )}
                    {Object.keys(fields.details).map((field, index) =>
                      fields.details[field]?.label ||
                      fields.details[field]?.defaultValue ? (
                        <LocalisedField
                          key={field}
                          section="details"
                          field={field}
                          fieldData={fields.details[field]}
                          language={selectedLanguage || '__pending__'}
                          showLabel={!!fields.details[field]?.label}
                          showDefaultValue={
                            !!fields.details[field]?.defaultValue
                          }
                          sectionName={`Details Field ${index + 1}`}
                          compact
                          disabled={!selectedLanguage}
                        />
                      ) : null
                    )}
                    {Object.keys(fields.back).map((field, index) =>
                      fields.back[field]?.label ||
                      fields.back[field]?.defaultValue ? (
                        <LocalisedField
                          key={field}
                          section="back"
                          field={field}
                          fieldData={fields.back[field]}
                          language={selectedLanguage || '__pending__'}
                          showLabel={!!fields.back[field]?.label}
                          showDefaultValue={!!fields.back[field]?.defaultValue}
                          sectionName={`Back Field ${index + 1}`}
                          compact
                          disabled={!selectedLanguage}
                        />
                      ) : null
                    )}
                  </Flex>
                  <Flex mt={6} alignItems="center" justify="flex-end">
                    <Link
                      variant="orange"
                      fontSize="12px"
                      onClick={() => {
                        setNewLanguage(false);
                        setSelectedLanguage('');
                      }}
                    >
                      Remove language
                    </Link>
                  </Flex>
                </Box>
              )}
              {!newLanguage && (
                <Tooltip
                  label="Maximum number of languages reached. Remove a language to add a new one."
                  isDisabled={!limitReached}
                  hasArrow
                  placement="top"
                  fontSize="12px"
                  bg="inkBlue"
                  color="white"
                  borderRadius="6px"
                  textAlign="center"
                  p="10px"
                >
                  <Box w="fit-content" mt={2}>
                    <AddMoreButton
                      text="Add another language"
                      isDisabled={limitReached}
                      onClick={() => {
                        setExpandedLanguage(null);
                        setNewLanguage(true);
                        scrollToNewLanguage();
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
            </>
          )}
        </Flex>
      </Collapse>
    </SectionWrapper>
  );
};

export default LanguagesFeature;
